import React, { useEffect } from 'react';
import { Footer } from './reports/_shared/Footer';
import { ArrowRight, ExternalLink, Youtube, Linkedin, Instagram } from 'lucide-react';
import WALL from './proof-wall.json';

/*
 * SocialSEO · Proof
 * Route: /proof
 *
 * Rebuilt 2026-09-21 from a five-seat council (designer, Hormozi, Shreya,
 * Ralston, red-teamer) and five blind reviewers. Record:
 * clients-current/suresh-malani/proof-page-council-2026-09-21.md
 *
 * Rules this page lives by:
 *  - every number opens to its source: a report, a screenshot, or a live
 *    profile pulled on AS_OF. Nothing projected, nothing client-reported
 *    without a link.
 *  - Instagram embeds stay playable.
 *  - the client wall loops all pages we have run, with the six we did not
 *    pull a count for shown honestly rather than padded.
 */

const AS_OF = '21 Sep 2026';
const WALL_COUNTED = WALL.filter((r) => r.f).length;
const WALL_TOTAL = WALL.reduce((s, r) => s + (r.f || 0), 0);

// ───────────────────────────── Instagram embed ─────────────────────────────

let embedRequested = false;
const loadEmbedScript = () => {
    if (window.instgrm && window.instgrm.Embeds) { window.instgrm.Embeds.process(); return; }
    if (embedRequested) return;
    embedRequested = true;
    const s = document.createElement('script');
    s.src = 'https://www.instagram.com/embed.js';
    s.async = true;
    s.onload = () => window.instgrm && window.instgrm.Embeds && window.instgrm.Embeds.process();
    document.body.appendChild(s);
};

// Fixed-height card: Instagram returns iframes of varying height, the player
// sits at the top, so only the caption tail is clipped behind the fade.
const IGEmbed = ({ url, who, handle, note, tall = false }) => {
    useEffect(() => { loadEmbedScript(); }, [url]);
    return (
        <figure className="w-full max-w-[540px] mx-auto">
            <div className={`relative rounded-2xl overflow-hidden border border-white/10 bg-black ${tall ? 'aspect-[2/3] sm:aspect-auto sm:h-[680px]' : 'h-[600px] sm:h-[640px]'}`}>
                <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={url}
                    data-instgrm-version="14"
                    style={{ background: '#000', margin: 0, maxWidth: '100%', minWidth: 0, width: '100%' }}
                >
                    <a href={url} target="_blank" rel="noopener noreferrer" className="block p-6 text-white/60 text-sm">Open on Instagram</a>
                </blockquote>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#080B11] to-transparent" />
            </div>
            <figcaption className="px-1 pt-3">
                <div className="text-white text-sm font-semibold leading-snug">{who}</div>
                {handle && (
                    <a href={`https://www.instagram.com/${handle}/`} target="_blank" rel="noopener noreferrer" className="text-indigo-300 text-xs underline decoration-dotted underline-offset-4 hover:text-white">@{handle}</a>
                )}
                {note && <p className="text-white/45 text-xs leading-snug mt-1">{note}</p>}
            </figcaption>
        </figure>
    );
};

// ───────────────────────────── primitives ─────────────────────────────

const Eyebrow = ({ children }) => (
    <p className="text-indigo-300/70 text-[10px] sm:text-xs uppercase tracking-[0.3em] font-bold mb-4">{children}</p>
);

const H2 = ({ children }) => (
    <h2 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight mb-3 text-white">{children}</h2>
);

const Sub = ({ children }) => (
    <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-8 max-w-2xl">{children}</p>
);

const Tile = ({ r }) => {
    const initials = r.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
    return (
        <a
            href={`https://www.instagram.com/${r.u}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 w-[248px] shrink-0 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 hover:bg-white/[0.08] transition-colors"
        >
            {r.a ? (
                <img src={r.a} alt="" loading="lazy" className="w-11 h-11 rounded-full object-cover shrink-0 bg-white/10" />
            ) : (
                <div className="w-11 h-11 rounded-full shrink-0 bg-gradient-to-br from-indigo-500/40 to-emerald-500/30 flex items-center justify-center text-xs font-bold text-white/80">{initials}</div>
            )}
            <div className="min-w-0 flex-1">
                <div className="text-white text-sm font-semibold leading-tight truncate">{r.name}</div>
                <div className="text-white/45 text-[11px] leading-tight truncate">@{r.u}</div>
            </div>
            <div className="text-right shrink-0">
                {r.f ? (
                    <>
                        <div className="text-white font-bold text-sm tabular-nums leading-tight">{r.f >= 1000000 ? `${(r.f / 1000000).toFixed(2)}M` : r.f >= 1000 ? `${(r.f / 1000).toFixed(r.f >= 100000 ? 0 : 1)}K` : r.f}</div>
                        <div className="text-white/35 text-[10px] leading-tight">followers</div>
                    </>
                ) : (
                    <div className="text-white/40 text-[11px] inline-flex items-center gap-1">profile <ArrowRight size={10} /></div>
                )}
            </div>
        </a>
    );
};

const MarqueeRow = ({ items, reverse = false, seconds = 80 }) => (
    <div className="group relative overflow-hidden">
        <div
            className="flex gap-3 w-max group-hover:[animation-play-state:paused]"
            style={{ animation: `proof-marquee ${seconds}s linear infinite`, animationDirection: reverse ? 'reverse' : 'normal' }}
        >
            {items.map((r) => <Tile key={r.u} r={r} />)}
            {items.map((r) => <span key={`${r.u}-dup`} aria-hidden="true"><Tile r={r} /></span>)}
        </div>
    </div>
);

const Founder = ({ name, handle, platform = 'ig', url, did, result, report, extra }) => (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-6 flex flex-col gap-3 h-full">
        <div>
            <h3 className="text-white font-bold text-base sm:text-lg leading-snug">{name}</h3>
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-300 text-sm underline decoration-dotted underline-offset-4 hover:text-white inline-flex items-center gap-1 break-all">
                {platform === 'yt' ? <Youtube size={13} /> : platform === 'li' ? <Linkedin size={13} /> : <Instagram size={13} />}
                {handle}
            </a>
        </div>
        <p className="text-slate-400 text-sm leading-relaxed">{did}</p>
        {result && <p className="text-emerald-300 text-sm font-semibold leading-snug">{result}</p>}
        {extra}
        {report && (
            <a href={report} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white">
                Open the report <ArrowRight size={12} />
            </a>
        )}
    </div>
);

// ───────────────────────────── page ─────────────────────────────

export default function Proof() {
    useEffect(() => {
        document.title = 'Proof | SocialSEO';
        window.scrollTo(0, 0);
    }, []);

    const third = Math.ceil(WALL.length / 3);
    const rows = [WALL.slice(0, third), WALL.slice(third, third * 2), WALL.slice(third * 2)];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-16 pt-24 relative z-10 min-h-screen">
            <style>{`
                @keyframes proof-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
                .proof-wall .group:hover [style*="proof-marquee"] { animation-play-state: paused !important; }
                @media (prefers-reduced-motion: reduce) {
                    .proof-wall [style*="proof-marquee"] { animation: none !important; }
                    .proof-wall .group { overflow-x: auto; }
                }
            `}</style>

            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
            </div>

            <header className="fixed top-0 left-0 right-0 z-50 px-5 py-4 bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center justify-between gap-3 w-full max-w-5xl mx-auto">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="font-bold tracking-tight text-white text-lg">Social<span className="text-indigo-400">SEO</span></span>
                        <span className="text-white/20 mx-1">/</span>
                        <span className="text-white/70 font-medium text-sm">Proof</span>
                    </div>
                    <span className="shrink-0 text-[10px] sm:text-xs text-white/50 border border-white/10 rounded-full px-2.5 py-1">Counts pulled {AS_OF}</span>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-5 pt-6 space-y-20">

                {/* HERO */}
                <section>
                    <Eyebrow>Founder pages we run</Eyebrow>
                    <h1 className="text-4xl sm:text-6xl font-black tracking-tight leading-[1.02] mb-6 text-white">
                        Every number here opens<br className="hidden sm:block" /> to the screen it came from.
                    </h1>
                    <p className="text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed">
                        Eight live profiles, the reels where clients said it on camera, and the two reports we sent those
                        clients. Three of the eight pages are under 2,000 followers and they sit on this page with the rest.
                        Nothing here is projected.
                    </p>
                </section>

                {/* FEATURED: Janvi reel + Insights screenshot */}
                <section id="janvi">
                    <Eyebrow>Start here</Eyebrow>
                    <H2>One month, and the screen it came off.</H2>
                    <Sub>Dr. Jaanvi Taneja's reel on the left. Her Instagram Insights on the right, as the app showed it.</Sub>
                    <div className="grid md:grid-cols-2 gap-6 items-start justify-items-center">
                        <IGEmbed
                            tall
                            url="https://www.instagram.com/reel/DWIhVlBv1Cu/"
                            who="Dr. Jaanvi Taneja, on the month in the screenshot beside this"
                            handle="jaanvitanejaspeaks"
                            note="Her words, her page, comments open. Three years working together."
                        />
                        <figure className="w-full max-w-[540px]">
                            <a href="/proof/janvi-135k-30-days.jpg" target="_blank" rel="noopener noreferrer" className="group block rounded-2xl overflow-hidden border border-white/10 bg-black">
                                <div className="relative w-full aspect-[1080/1830]">
                                    <img src="/proof/janvi-136k-crop.jpg" alt="Instagram Insights: 287,402 followers, up 90.8 percent, overall followers +136,807, 29 Jan to 27 Feb" loading="lazy" className="absolute inset-0 w-full h-full object-contain" />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors flex items-center justify-center">
                                        <ExternalLink size={20} className="text-white/0 group-hover:text-white/80 transition-colors" />
                                    </div>
                                </div>
                            </a>
                            <figcaption className="px-1 pt-3">
                                <div className="text-white text-sm font-semibold leading-snug">Her Instagram Insights, 29 Jan to 27 Feb</div>
                                <p className="text-white/45 text-xs leading-snug mt-1">
                                    Followers 287,402, up 90.8%. Overall followers +136,807. A mass-consumer education page that was
                                    already large, which is why the curve is steep. Trimmed only below the chart; tap to open the full screenshot.
                                </p>
                            </figcaption>
                        </figure>
                    </div>
                </section>

                {/* THE WALL */}
                <section className="proof-wall">
                    <Eyebrow>Pages we have run</Eyebrow>
                    <H2>{WALL.length} pages, still scrolling.</H2>
                    <Sub>
                        Founders, D2C brands, sports leagues and media pages, every one a page we ran. {WALL_COUNTED} counts
                        pulled live on {AS_OF}, {(WALL_TOTAL / 1000000).toFixed(2)}M followers across them. The
                        {' '}{WALL.length - WALL_COUNTED} without a number are pages we did not pull a count for. Hover to pause, tap any tile to open it.
                    </Sub>
                    <div className="space-y-3 -mx-5 px-5 [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
                        <MarqueeRow items={rows[0]} seconds={75} />
                        <MarqueeRow items={rows[1]} seconds={95} reverse />
                        <MarqueeRow items={rows[2]} seconds={85} />
                    </div>
                </section>

                {/* CLIENT INTERVIEWS */}
                <section>
                    <Eyebrow>Client interviews</Eyebrow>
                    <H2>Our format. Their words.</H2>
                    <Sub>Recorded with the client and posted from our page, so the person is named under each one and you can open the comments.</Sub>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start justify-items-center">
                        <IGEmbed url="https://www.instagram.com/reel/DZKKEhjP_jh/" who="Dr. Jaanvi Taneja" handle="jaanvitanejaspeaks" note="Three years on the same account." />
                        <IGEmbed url="https://www.instagram.com/reel/DWhBNWtP_27/" who="The Mask Podcast with Abhiveer" handle="themaskwithabhiveer" note="One month in. The 30-day report is linked below." />
                        <IGEmbed url="https://www.instagram.com/reel/DYr8MoFve7x/" who="Radha Patel and Abhiveer" handle="theradhapatel" note="Two founders, one session." />
                        <IGEmbed url="https://www.instagram.com/reel/DUk86IJD8jy/" who="Kush Desai, the case study reel" handle="realkushdesai" note="His dated numbers are in the card below." />
                        <IGEmbed url="https://www.instagram.com/reel/Da7qiEGj_MR/" who="How the weekly session runs" note="One Tuesday, thirty minutes to an hour, then we do the rest." />
                    </div>
                </section>

                {/* OUR OWN PAGE */}
                <section>
                    <Eyebrow>We run this on our own page first</Eyebrow>
                    <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 items-center">
                        <div>
                            <H2>One reel. 235,264 plays. 7,414 comments.</H2>
                            <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-4">
                                Every one of those comments is a single keyword, asking for the resource named in the video.
                                A comment triggers a DM with the link. That comment-to-DM engine, built on our Meta-verified app,
                                is the same one we install on every client page. The mechanism is the product; the reel is the demo.
                            </p>
                            <a href="https://www.instagram.com/sureshmalanii" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-indigo-300 text-sm underline decoration-dotted underline-offset-4 hover:text-white">
                                <Instagram size={14} /> @sureshmalanii
                            </a>
                        </div>
                        <IGEmbed url="https://www.instagram.com/reel/DMnPjLUOFe7/" who="Suresh Malani" handle="sureshhmalani" note="Plays and comments read off the post on 21 Sep 2026." />
                    </div>
                </section>

                {/* FOUNDERS */}
                <section id="founders">
                    <Eyebrow>The founders</Eyebrow>
                    <H2>Open any profile. They are all live.</H2>
                    <Sub>A result appears only where a report or a post backs it. Where we have no number, the card says what we did and stops.</Sub>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Founder
                            name="Kush Desai"
                            handle="@realkushdesai"
                            url="https://www.instagram.com/realkushdesai/"
                            did="Founder, Source Code Lab. AI and B2B lead-gen content for tier-one buyers, English only. He never sat for a recording; the AI clone carries his face and voice. We write and post every piece."
                            result="Mar 1 to Apr 30, 2026: 17.4K to 22.2K followers, 1.05M Instagram views, top reel 198K, plus 1.37M on YouTube. Live on 21 Sep 2026: 39,772."
                            report="/reports/kush/report6"
                        />
                        <Founder
                            name="The Mask Podcast with Abhiveer"
                            handle="@themaskwithabhiveer"
                            url="https://www.instagram.com/themaskwithabhiveer/"
                            did="Long podcast episodes cut into founder clips. Athletes, founders, warriors."
                            result="Feb 24 to Mar 24, 2026: +220% followers, 830K+ views, nine viral reels."
                            report="/reports/abhiveer/30-day"
                        />
                        <Founder
                            name="Dr. Jaanvi Taneja"
                            handle="@jaanvitanejaspeaks"
                            url="https://www.instagram.com/jaanvitanejaspeaks/"
                            did="Communication and career growth for India. Three years on the same account."
                            result="+136,807 followers, 29 Jan to 27 Feb. The screenshot at the top of this page."
                            extra={<a href="#janvi" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white">See the screenshot <ArrowRight size={12} /></a>}
                        />
                        <Founder
                            name="Nidhi Katiyar"
                            handle="@nidhi.log"
                            url="https://www.instagram.com/nidhi.log/"
                            did="D2C beauty founder with an established main page. This second page started at zero and speaks to founders building e-commerce brands."
                            result="Top reel on the new page: 20,637 plays, posted 12 Sep 2026."
                            extra={
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <a href="https://www.instagram.com/p/DdLYcHQgmTD/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"><Instagram size={12} /> that reel</a>
                                    <a href="https://www.instagram.com/nidhi.167/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"><Instagram size={12} /> @nidhi.167, main page</a>
                                    <a href="https://www.youtube.com/@NidhiK" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"><Youtube size={12} /> @NidhiK</a>
                                </div>
                            }
                        />
                        <Founder
                            name="Shreya Pattar"
                            handle="@shreyapattar"
                            url="https://www.instagram.com/shreyapattar/"
                            did="LinkedIn ghostwriter with a freelancer audience and a founder offer. We script the move to founders. The commenter mix is the KPI, and we will publish it when there is a report."
                            extra={<a href="https://www.linkedin.com/in/shreyapattar" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"><Linkedin size={12} /> linkedin.com/in/shreyapattar</a>}
                        />
                        <Founder
                            name="Dr. Debmita Dutta"
                            handle="@debmitadutta"
                            url="https://www.instagram.com/debmitadutta/"
                            did="Doctor and parenting consultant. We run the weekly research and idea loop. Her team scripts and shoots."
                        />
                        <Founder
                            name="Radha Patel"
                            handle="@theradhapatel"
                            url="https://www.instagram.com/theradhapatel/"
                            did="Founder Diaries on the personal page, and an animated kids IP we produce end to end."
                        />
                        <Founder
                            name="Source Code Lab"
                            handle="@sourcecodelab"
                            url="https://www.instagram.com/sourcecodelab/"
                            did="Kush's company page, taken on after month three. iGaming, a small audience by design, so the viewers are the buyers. Company content ran to August 2026; his founder page continues."
                        />
                    </div>
                </section>

                {/* WHAT WE CANNOT SHOW */}
                <section>
                    <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-6 sm:p-7 max-w-3xl">
                        <div className="text-amber-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-2">What this page cannot show</div>
                        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                            Pipeline, meetings and revenue. We do not hold our clients' CRMs, so those numbers are theirs to share,
                            and we do not quote them second-hand. What we can show is the audience, the conversations it starts, and
                            the reports we send. Where we have no number, we say so.
                        </p>
                    </div>
                </section>

                {/* CAMPAIGNS */}
                <section>
                    <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-[#0F172A] p-7 sm:p-9">
                        <Eyebrow>Beyond founders</Eyebrow>
                        <H2>The brand campaigns sit on a page of their own.</H2>
                        <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-5 max-w-2xl">
                            373M views in 30 days for a cricket league, a fan channel built from zero, one political page.
                            Different work from founder pages, so it lives apart, with every screenshot next to it.
                        </p>
                        <a href="/case-studies" className="inline-flex items-center gap-2 text-white font-bold text-sm bg-indigo-500/20 border border-indigo-400/30 rounded-xl px-4 py-2.5 hover:bg-indigo-500/30 transition-colors">
                            Open the campaign case studies <ArrowRight size={14} />
                        </a>
                    </div>
                </section>

                {/* CLOSE */}
                <section className="text-center">
                    <H2>Send us a profile.</H2>
                    <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto mb-6">
                        Every profile above is live right now. Open two of them and judge the last thirty days yourself. Then send
                        suresh@socialseo.in the founder page you want read, and we send back what we would change inside two working days.
                    </p>
                    <a href="https://www.linkedin.com/posts/sureshmalani_socialseo-share-7475245326421905409-W-OD/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-indigo-300 text-sm underline decoration-dotted underline-offset-4 hover:text-white">
                        <Linkedin size={14} /> Suresh Malani on LinkedIn
                    </a>
                </section>

                <Footer />
            </main>
        </div>
    );
}
